import { effect, Injectable, signal, WritableSignal } from '@angular/core';
import { environment } from "../../../environments/environment";
import { HttpClient } from "@angular/common/http";
import { CurrentUser, User } from "../../interfaces/user";
import { tap } from "rxjs";
import { UserRole } from "../../interfaces/role";
import { Router } from "@angular/router";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private api: string = `${environment.api}/auth`;
  currentUser: WritableSignal<CurrentUser | null> = signal(null);

  constructor(private http: HttpClient,
              private router: Router) {
    this.loadUser();
    effect(() => localStorage.setItem('current_user', JSON.stringify(this.currentUser())));
  }

  private loadUser() {
    const storedUser = localStorage.getItem('current_user');
    this.currentUser.set(storedUser ? JSON.parse(storedUser) : null);
  }

  public updateUser(user: User) {
    this.currentUser.update(current => {
      if (!current) return current;

      return {
        ...current,
        user: { ...current.user, ...user }
      };
    });
    localStorage.setItem('current_user', JSON.stringify(this.currentUser()))
  }

  public login(email: string, password: string) {
    return this.http.post<CurrentUser>(`${this.api}/login`, { email, password }, { observe: 'body' }).pipe(
      tap(res => this.currentUser.set(res)),
    );
  }

  public logout() {
    this.http.post(`${this.api}/logout`, {}, { observe: 'response' }).subscribe();
    localStorage.clear();
    this.currentUser.set(null);
    this.router.navigate(['/login'])
  }

  public sendForgotPassword(email: string) {
    return this.http.post(`${this.api}/forgot_password`, { email }, { observe: 'response' });
  }

  public resetPassword(token: string, password: string) {
    return this.http.post(`${this.api}/reset_password`, { token, password }, { observe: 'response' });
  }

  public verifyEmail(token: string) {
    return this.http.post(`${this.api}/verify_email`, { token }, { observe: 'response' });
  }

  public resendVerification(email: string) {
    return this.http.post(`${this.api}/verify_email`, { email }, { observe: 'response' });
  }

  public validateSession() {
    return this.http.get(`${this.api}/validate`, { observe: 'response' });
  }

  public get isAuthenticated(): boolean {
    return !!this.currentUser();
  }

  public get authToken(): string | undefined {
    return this.currentUser()?.token;
  }

  public get userRole(): UserRole | undefined {
    return this.currentUser()?.user.role.name;
  }

  public get isVerified(): boolean {
    return !!this.currentUser()?.user.email_verified_at;
  }

  public get redirectPath(): string {
    switch (this.userRole) {
      case UserRole.ADMIN        :
        return 'admin'
      case UserRole.TEACHER      :
        return 'teacher'
      case UserRole.STUDENT      :
        return 'student'
      case UserRole.SUPER_ADMIN  :
        return 'super-admin'
      case UserRole.NON_TEACHING :
        return 'admin'
      default                    :
        return ''
    }
  }
}
